/**
 * フォントスペック型と既定フォント一覧。
 *
 * happa v1.1 の混植（Latin / Japanese 自動振分）と FontFace API による
 * フォルダ読み込みのために、`AppState` から参照される型と既定値を提供する。
 *
 * 設計参照:
 * - spec §5.2（FontSpec）
 * - spec §5.5（DEFAULT_FONTS / loadFontFolder）
 * - spec §6（state 内 `userFonts`）
 *
 * 責務分離:
 * - 本ファイル: 型と既定値のみ。DOM / FontFace API には触らない。
 * - `loadFontFolder` 等の UI 側処理: ui-agent が `src/ui/fontPicker.ts` で実装。
 *
 * 後続予定: spec §9 のプリセットテキスト一覧は `src/state/presets.ts` に
 * 別途実装する（本フェーズでは未着手）。
 */

/**
 * フォント 1 件の表現（spec §5.2）。
 *
 * `default`（システムフォント or 既定スタック）と `user`（フォルダ読み込みで
 * FontFace API に登録された動的フォント）を統一的に扱うための値オブジェクト。
 */
export interface FontSpec {
  /**
   * CSS `font-family` 文字列（そのまま `ctx.font` の家族部に使用可能）。
   * 例: `"ui-monospace, 'SF Mono', Menlo, Consolas, monospace"`、
   *     `'"user_MyFont_1234567890_0"'`（user 起源、引用符含む）。
   */
  family: string;

  /**
   * UI 表示名。dropdown のラベルとして表示される。
   * user 起源のフォントは末尾に `★` を付ける慣例（spec §5.5）。
   */
  name: string;

  /**
   * 由来。`'default'` = 本ファイルの `DEFAULT_FONTS`、
   * `'user'` = フォルダ読み込みで登録された FontFace。
   */
  source: 'default' | 'user';
}

/**
 * 既定フォント一覧（spec §5.5）。
 *
 * 7 件構成:
 * - Mono (System) / Sans (System) / Helvetica … Latin 系既定スタック
 * - Hiragino Kaku Gothic / Hiragino Mincho / Yu Gothic / Yu Mincho … 和文既定
 *
 * フォルダ読み込み済みの `userFonts` と合わせて UI dropdown に表示される。
 * 順序は dropdown 表示順そのもの。並びを変えると UX が変わるため変更時は周知。
 */
export const DEFAULT_FONTS: readonly FontSpec[] = [
  {
    family: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
    name: 'Mono (System)',
    source: 'default',
  },
  {
    family: '-apple-system, BlinkMacSystemFont, sans-serif',
    name: 'Sans (System)',
    source: 'default',
  },
  {
    family: "'Helvetica Neue', Helvetica, Arial, sans-serif",
    name: 'Helvetica',
    source: 'default',
  },
  {
    family: "'Hiragino Kaku Gothic ProN', 'Hiragino Sans', 'Noto Sans JP', sans-serif",
    name: 'Hiragino Kaku Gothic',
    source: 'default',
  },
  {
    family: "'Hiragino Mincho ProN', 'Noto Serif JP', serif",
    name: 'Hiragino Mincho',
    source: 'default',
  },
  {
    family: "'Yu Gothic', 'YuGothic', sans-serif",
    name: 'Yu Gothic',
    source: 'default',
  },
  {
    family: "'Yu Mincho', 'YuMincho', serif",
    name: 'Yu Mincho',
    source: 'default',
  },
];

/**
 * フォントファイルのベースネームを CSS family 名に安全な形へ正規化する。
 *
 * 英数字以外を `_` に置換し、最大 32 文字で切り詰める。空文字になった場合は
 * `'font'` を返す（CSS family として不正な「空の識別子」を避けるため）。
 *
 * 用途: `src/ui/fontLoader.ts` が `user_<sanitized>_<timestamp>_<index>`
 * 形式の一意な family 名を生成するための補助。FontFace 登録前の前処理として
 * 呼ばれる。
 *
 * spec §5.5 / OQ-17 関連。DOM API には触れない pure 関数なので state 層に置く。
 *
 * @example
 * sanitizeFontBasename('MyFont-Regular.otf'); // => 'MyFont_Regular_otf'
 * sanitizeFontBasename('日本語.ttf');         // => '_ttf'
 * sanitizeFontBasename('');                   // => 'font'
 *
 * @param name - 任意の文字列（典型的にはフォントファイル名からの basename）
 * @returns 英数字と `_` のみからなる、1 〜 32 文字の識別子
 */
export function sanitizeFontBasename(name: string): string {
  const sanitized = name.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 32);
  return sanitized.length === 0 ? 'font' : sanitized;
}
